import { ExpandLess } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandMore from "@mui/icons-material/ExpandMore";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import InfoIcon from "@mui/icons-material/Info";
import LoadingButton from "@mui/lab/LoadingButton";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import {
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  useGetAccountOnboardingStatusQuery,
  useGetAccountQuery,
  useGetAccountRelationshipQuery,
  useGetDocumentsQuery,
  useGetPartnerContactsQuery,
  useSubmitAccountMutation,
} from "api/api-accounts";
import { areMandatoryRequiredDocsComplete } from "components/Company/Documents/financialDocumentHelpers";
import { BookMeetingTabParam } from "helpers/bookMeetingConstants";
import { buildBookMeetingUrl } from "helpers/bookMeetingHelpers";
import {
  getOnboardingStepFlags,
  isDocumentsMandatoryComplete,
  isOnboardingReadyToSubmit,
  isPartnerProfileComplete,
  isPartnerUsersReadyForSubmit,
} from "helpers/onboardingSubmitHelpers";
import { useGetBankAccountsQuery } from "api/api-banks";

import {
  BUYER,
  CL_APPROVED,
  CL_REJECTED,
  PRODUCTS,
  REJECTED_BY_COUNTRY_MATRIX,
  SELLER,
  TR_LINK,
} from "codes";
import SkeletonLoad from "components/Common/SkeletonLoad";
import {
  isReverseFactoringSupplierAllocationApproved,
  isReverseFactoringSupplierPartnerClApproved,
} from "components/Company/Lead/utils/reverseFactoringFlow";
import { withEffectiveCreditLimitStatus } from "helpers/creditLimitStatus";
import history from "helpers/history";
import useIsMobile from "hooks/useIsMobile";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppSelector } from "redux/hooks";
import type { CreditLimit } from "types";

interface PartnerData {
  id: string;
  name: string;
  reg_no: string;
  termsheetSigned: boolean;
  country: string;
  profile?: string;
  documents_completed: boolean;
  clAmount: number | CreditLimit;
  approval: string;
  credit_limit: CreditLimit;
  product?: string;
  onboarding_submitted?: boolean;
  onboardingSubmitted?: boolean;
  call_status: boolean | null;
  document_reviewed: boolean;
  document_reviewed_risk: boolean;
  document_reviewed_operation: boolean;
}

const SignedPartner = ({
  partner,
  viewerOwnCreditLimit,
}: {
  partner: PartnerData;
  /** Reverse-factoring buyer: CL status is on the buyer account, not the listed supplier row. */
  viewerOwnCreditLimit?: CreditLimit | null;
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const profile = useAppSelector((state) => state.appState.profile);
  const normalizedProfile = String(profile ?? "").toLowerCase();
  const hasOwnClRow = Boolean(
    viewerOwnCreditLimit &&
      typeof viewerOwnCreditLimit === "object" &&
      "id" in viewerOwnCreditLimit &&
      viewerOwnCreditLimit.id,
  );
  const isReverseFactoringRelationship =
    String(partner?.product ?? "").toLowerCase() === "reverse_factoring";
  const rfBuyerLeadContext =
    isReverseFactoringRelationship && hasOwnClRow
      ? {
          account_product: PRODUCTS.reverse_factoring,
          profile: BUYER,
          credit_limit: viewerOwnCreditLimit ?? undefined,
        }
      : undefined;
  const rfPartnerForClCheck = {
    profile: SELLER,
    credit_limit: partner.credit_limit,
    country_matrix_blocked: (partner as { country_matrix_blocked?: boolean })
      .country_matrix_blocked,
  };
  const isSupplierAllocationApproved = Boolean(
    rfBuyerLeadContext &&
      isReverseFactoringSupplierAllocationApproved({
        lead: rfBuyerLeadContext,
        partner: rfPartnerForClCheck,
      }),
  );
  const isRfBuyerClApprovedForPartner = Boolean(
    rfBuyerLeadContext &&
      isReverseFactoringSupplierPartnerClApproved({
        lead: rfBuyerLeadContext,
        partner: rfPartnerForClCheck,
        requireSupplierAllocation: false,
      }),
  );
  const normalizedPartnerCreditLimit = withEffectiveCreditLimitStatus(
    partner.credit_limit,
  );
  const effectiveCreditLimit =
    (isSupplierAllocationApproved || isRfBuyerClApprovedForPartner) &&
    normalizedPartnerCreditLimit
      ? { ...normalizedPartnerCreditLimit, status: CL_APPROVED }
      : normalizedPartnerCreditLimit;
  const USER_ROLE = useAppSelector((state) => state.appState.role);
  const [updateSubmitStatus, { isUninitialized, isLoading }] =
    useSubmitAccountMutation();

  const { data: relationship, isFetching: isFetchingRelationship } =
    useGetAccountRelationshipQuery(
      { companyId: USER_ROLE?.id, partnerId: partner?.id },
      {
        skip: !Boolean(USER_ROLE.id) || !Boolean(partner.id),
        refetchOnFocus: true,
        refetchOnReconnect: true,
      },
    );

  const { data: banks, isFetching: isFetchingBanks } = useGetBankAccountsQuery(
    partner?.id,
    {
      refetchOnFocus: true,
      skip: !Boolean(partner?.id) || normalizedProfile === SELLER,
    },
  );

  const accountId = USER_ROLE.id;
  const partnerAccountId = partner.id;

  const { data: users, isFetching: isFetchingUsers } =
    useGetPartnerContactsQuery(
      { companyId: accountId, partnerId: partnerAccountId },
      { skip: !partnerAccountId || !accountId },
    );
  const { data: account, isFetching: isFetchingCompany } = useGetAccountQuery(
    partner.id,
    { skip: !partner.id },
  );
  const { data: onboardingStatus, isFetching: isFetchingOnboardingStatus } =
    useGetAccountOnboardingStatusQuery(partner?.id, {
      skip: !Boolean(partner?.id),
      refetchOnFocus: true,
      refetchOnReconnect: true,
    });

  const { data: pendingDocs } = useGetDocumentsQuery(
    { account_id: partner?.id },
    { skip: !Boolean(partner?.id) },
  );

  const {
    isDocumentsStepComplete,
    isDetailsStepComplete,
    isUsersStepComplete,
    isBankDetailsStepComplete,
  } = getOnboardingStepFlags(onboardingStatus);
  const partnerCompanyType =
    partner?.profile?.toLowerCase() ||
    account?.profile?.toLowerCase() ||
    (normalizedProfile === SELLER ? BUYER.toLowerCase() : SELLER);
  const areRequiredDocumentsUploaded = areMandatoryRequiredDocsComplete(
    pendingDocs?.data,
    {
      account_product: account?.account_product,
      companyType: partnerCompanyType,
    },
  );
  const documentsMandatoryComplete = isDocumentsMandatoryComplete({
    isDocumentsStepComplete,
    pendingDocsComplete: pendingDocs?.complete,
    areRequiredDocumentsUploaded,
  });
  const isDocumentsReviewed =
    (account?.document_reviewed_operation === true &&
      account?.document_reviewed_risk === true) ||
    (partner.document_reviewed_operation === true &&
      partner.document_reviewed_risk === true);
  const isUsersReadyForSubmit = isPartnerUsersReadyForSubmit({
    isUsersStepComplete,
    hasContacts: Boolean(users?.length),
  });
  const isPartnerReadyToSubmit = isOnboardingReadyToSubmit({
    isProfileComplete: isPartnerProfileComplete({
      isDetailsStepComplete,
      isUsersStepComplete: isUsersReadyForSubmit,
      isBankDetailsStepComplete,
      normalizedProfile,
    }),
    isTermsheetSigned: Boolean(
      partner?.termsheetSigned || relationship?.is_termsheet_signed,
    ),
    documentsMandatoryComplete,
    isDocumentsReviewed,
  });

  const documentsTodoPrimary = isDocumentsReviewed
    ? "Documents uploaded!"
    : documentsMandatoryComplete
      ? "Documents uploaded successfully!"
      : "Please upload representative Trade Documents.";

  const documentsStatusIcon = isDocumentsReviewed ? (
    <CheckCircleIcon color="success" />
  ) : documentsMandatoryComplete ? (
    <WatchLaterIcon color="action" fontSize="small" />
  ) : (
    <ErrorIcon color="error" />
  );

  const handleClick = () => {
    setOpen(!open);
  };

  const isCompanyAgreed =
    account?.agree_to_terms && account?.agree_to_privacy_policy;
  const isMobile = useIsMobile();
  const handleSubmission = () => {
    if (isCompanyAgreed) {
      isUninitialized &&
        updateSubmitStatus({
          accountId: USER_ROLE.id,
          data: { is_submitted: true, partner_id: partner.id },
        })
          .unwrap()
          .catch((error) => {
            const errorFile = error?.message;
            toast(`Something went wrong : ${errorFile}`, {
              type: "error",
            });
          });
    } else {
      history.push(`/submission`, { entity: partner, isPartner: true });
    }
  };

  // Book Meeting logic for partner
  const isCLApproved = effectiveCreditLimit?.status === CL_APPROVED;
  const callStatus = partner?.call_status;

  // Show the booking CTA until this partner's Credit Limit is approved.
  const shouldShowBookMeeting = !isCLApproved;
  const canBookMeeting =
    !isCLApproved && (callStatus === false || callStatus === null);

  const handleBookMeeting = () => {
    window.open(
      buildBookMeetingUrl({
        tab: BookMeetingTabParam.RISK_TEAM,
        accountId: partner?.id,
      }),
      "_blank",
      "noopener,noreferrer",
    );
  };

  const SubmitButton = () => (
    <LoadingButton
      variant="contained"
      fullWidth
      color="success"
      onClick={handleSubmission}
      disabled={!isPartnerReadyToSubmit}
      loading={isLoading}
      data-testid={`submit-partner-${partner?.name}`}
    >
      SUBMIT
    </LoadingButton>
  );

  return (
    <Grid container>
      <Grid item xs={12}>
        <ListItemButton
          data-testid={`listitem-partner-${partner?.name}`}
          onClick={handleClick}
          sx={{
            pl: 1.5,
            border: "1px solid rgb(224, 224, 224)",
            borderRadius: "10px",
            mb: "0em",
            bgcolor: "rgba(244, 244, 244, 0.7)",
          }}
        >
          <Grid
            container
            spacing={1}
            alignItems="start"
            justifyContent="space-between"
          >
            <Grid item xs={12} lg={shouldShowBookMeeting ? 6 : 8}>
              <Grid
                container
                spacing={1}
                alignItems="end"
                justifyContent="start"
              >
                <Grid item>
                  <IconButton
                    sx={{
                      borderRadius: "50%",
                      background: "#bcbcbc",
                      boxShadow:
                        "4px 4px 30px 0px #bdbdbd, -15px -17px 30px 0px #ffffff",
                    }}
                  >
                    {open ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Grid>
                <Grid item>
                  {normalizedProfile === SELLER && (
                    <ListItemIcon sx={{ minWidth: 10 }}>
                      {isPartnerReadyToSubmit ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </ListItemIcon>
                  )}
                  {normalizedProfile === BUYER.toLowerCase() && (
                    <ListItemIcon>
                      {isPartnerReadyToSubmit ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </ListItemIcon>
                  )}
                </Grid>
                <Grid item>
                  <ListItemText
                    primary={`${partner.name}`}
                    primaryTypographyProps={{ fontWeight: "800" }}
                  />
                </Grid>

                {/* Submitted chip only when THIS partner's relationship is submitted (relationship is per companyId+partnerId) */}
                {Boolean(relationship?.is_submitted) && (
                  <Grid item data-testid={`submitted-partner-${partner?.name}`}>
                    <Chip
                      label="Submitted"
                      color="success"
                      variant="outlined"
                      sx={{
                        fontSize: "0.875em",
                        height: "2.5em",
                        fontWeight: "bold",
                      }}
                    />
                  </Grid>
                )}
                {!relationship?.is_submitted &&
                  effectiveCreditLimit?.status !== CL_APPROVED &&
                  !(
                    effectiveCreditLimit?.status === CL_REJECTED &&
                    effectiveCreditLimit?.internal_status ===
                      REJECTED_BY_COUNTRY_MATRIX
                  ) && (
                    <Grid item sx={{ py: "0 !important", mb: "0.4em" }}>
                      <Typography>
                        {effectiveCreditLimit?.status === CL_REJECTED
                          ? t("dashboard-partner-cl-rejected-generic")
                          : t("dashboard-partner-cl-pending")}
                      </Typography>
                    </Grid>
                  )}

                {!relationship?.is_submitted &&
                  effectiveCreditLimit?.status === CL_APPROVED && (
                    <Grid item sx={{ py: "0 !important", mb: "0.4em" }}>
                      <Typography>
                        {partner?.termsheetSigned
                          ? "is having an approved termsheet."
                          : "is pending termsheet approval."}
                      </Typography>
                    </Grid>
                  )}
              </Grid>
            </Grid>
            <Grid item xs={6} lg={2}>
              <Button
                variant="contained"
                fullWidth
                onClick={() =>
                  history.push(`${TR_LINK}/profile/${partner?.id}`)
                }
                data-testid={`view-partner-${partner?.name}`}
              >
                View
              </Button>
            </Grid>
            {!isFetchingRelationship && !relationship?.is_submitted && (
              <Grid item xs={6} lg={2}>
                <SubmitButton />
              </Grid>
            )}
            {shouldShowBookMeeting && (
              <Grid item xs={6} lg={2}>
                <Button
                  variant="contained"
                  fullWidth
                  color={callStatus === true ? "inherit" : "primary"}
                  startIcon={<CalendarMonth />}
                  onClick={handleBookMeeting}
                  disabled={!canBookMeeting}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {callStatus === true ? "Meeting Done" : "Book Meeting"}
                </Button>
              </Grid>
            )}
          </Grid>
        </ListItemButton>
      </Grid>
      <Grid item xs={12}>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div">
            <ListItemButton
              sx={{
                pl: isMobile ? 1 : 3,
                pr: isMobile ? 1 : 2,
                border: "1px solid rgb(224, 224, 224)",
                borderRadius: "10px",
                mb: "0.5em",
              }}
            >
              <Grid container alignItems="center" justifyContent="start">
                <Grid item xs={12} lg={10}>
                  <Grid
                    container
                    spacing={1}
                    alignItems="center"
                    justifyContent="start"
                  >
                    <Grid item xs={2}>
                      <ListItemIcon>
                        {partner?.termsheetSigned ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <ErrorIcon color="error" />
                        )}
                      </ListItemIcon>
                    </Grid>
                    <Grid item>
                      {partner?.termsheetSigned ? (
                        <ListItemText primary="Termsheet Approved" />
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ListItemText primary="Please approve termsheet" />
                          <Tooltip title="Termsheet has been sent to your registered mailbox for your signatory & approval">
                            <div>
                              <InfoIcon style={{ marginTop: "6px" }} />
                            </div>
                          </Tooltip>
                        </Stack>
                      )}

                      <Grid item></Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </ListItemButton>

            <ListItemButton
              sx={{
                pl: isMobile ? 1 : 3,
                pr: isMobile ? 1 : 2,
                border: "1px solid rgb(224, 224, 224)",
                borderRadius: "10px",
                mb: "0.5em",
              }}
            >
              {isFetchingCompany && <SkeletonLoad bars={1} />}
              {!isFetchingCompany && (
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} lg={10}>
                    <Grid
                      container
                      spacing={1}
                      alignItems="center"
                      justifyContent="start"
                    >
                      <Grid item xs={2}>
                        <ListItemIcon>
                          {onboardingStatus?.details?.status ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <ErrorIcon color="error" />
                          )}
                        </ListItemIcon>
                      </Grid>
                      <Grid item xs={10} lg={10}>
                        <ListItemText
                          primary={
                            onboardingStatus?.details?.status
                              ? "Company details completed!"
                              : "Please complete company details."
                          }
                          secondary={onboardingStatus?.details?.info}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} lg={2}>
                    {!onboardingStatus?.details?.status && (
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() =>
                          history.push(`${TR_LINK}/profile/${partner?.id}`, {
                            tab: 0,
                          })
                        }
                      >
                        ADD
                      </Button>
                    )}
                  </Grid>
                </Grid>
              )}
            </ListItemButton>
            <ListItemButton
              sx={{
                pl: isMobile ? 1 : 3,
                pr: isMobile ? 1 : 2,
                border: "1px solid rgb(224, 224, 224)",
                borderRadius: "10px",

                mb: "0.5em",
              }}
            >
              {isFetchingOnboardingStatus && <SkeletonLoad bars={1} />}
              {!isFetchingOnboardingStatus && (
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} lg={10}>
                    <Grid
                      container
                      spacing={1}
                      alignItems="center"
                      justifyContent="start"
                    >
                      <Grid item xs={2}>
                        <ListItemIcon>{documentsStatusIcon}</ListItemIcon>
                      </Grid>
                      <Grid item xs={12} lg={10}>
                        <ListItemText
                          primary={documentsTodoPrimary}
                          secondary={
                            documentsMandatoryComplete
                              ? !isDocumentsReviewed
                                ? "Pending for document review by incomlend internal team"
                                : ""
                              : onboardingStatus?.documents?.info
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} lg={2}>
                    {!documentsMandatoryComplete && (
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() =>
                          history.push(`${TR_LINK}/profile/${partner?.id}`, {
                            tab: 1,
                          })
                        }
                      >
                        UPLOAD
                      </Button>
                    )}
                  </Grid>
                </Grid>
              )}
            </ListItemButton>
            {profile === BUYER.toLowerCase() && (
              <ListItemButton
                sx={{
                  pl: isMobile ? 1 : 3,
                  pr: isMobile ? 1 : 2,
                  border: "1px solid rgb(224, 224, 224)",
                  borderRadius: "10px",

                  mb: "0.5em",
                }}
              >
                {isFetchingBanks && <SkeletonLoad bars={1} />}
                {!isFetchingBanks && (
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} lg={10}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={2}>
                          <ListItemIcon>
                            {onboardingStatus?.bank_details?.status ? (
                              <CheckCircleIcon color="success" />
                            ) : (
                              <ErrorIcon color="error" />
                            )}
                          </ListItemIcon>
                        </Grid>
                        <Grid item xs={12} lg={10}>
                          <ListItemText
                            primary={
                              Boolean(banks?.length)
                                ? onboardingStatus?.bank_details?.status
                                  ? "Bank account registered!"
                                  : "Please enter Bank Account details."
                                : "Please register a bank account."
                            }
                            secondary={onboardingStatus?.bank_details?.info}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    {Boolean(banks?.length) &&
                      !onboardingStatus?.bank_details?.status && (
                        <Grid item xs={12} lg={2}>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() =>
                              history.push(
                                `${TR_LINK}/profile/${partner?.id}`,
                                {
                                  tab: 2,
                                },
                              )
                            }
                          >
                            COMPLETE
                          </Button>
                        </Grid>
                      )}
                    {!Boolean(banks?.length) && (
                      <Grid item xs={12} lg={2}>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() =>
                            history.push(`${TR_LINK}/profile/${partner?.id}`, {
                              tab: 3,
                            })
                          }
                        >
                          REGISTER
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                )}
              </ListItemButton>
            )}

            <ListItemButton
              sx={{
                pl: isMobile ? 1 : 3,
                pr: isMobile ? 1 : 2,
                border: "1px solid rgb(224, 224, 224)",
                borderRadius: "10px",

                mb: "0.5em",
              }}
            >
              {isFetchingUsers && <SkeletonLoad bars={1} />}
              {!isFetchingUsers && (
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} lg={10}>
                    <Grid
                      container
                      spacing={1}
                      alignItems="center"
                      justifyContent="start"
                    >
                      <Grid item xs={2}>
                        <ListItemIcon>
                          {isUsersReadyForSubmit ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <ErrorIcon color="error" />
                          )}
                        </ListItemIcon>
                      </Grid>
                      <Grid item>
                        <ListItemText
                          primary={
                            !Boolean(users?.length)
                              ? "Please register a user."
                              : isUsersReadyForSubmit
                                ? "Primary Contact defined!"
                                : "Please complete user details."
                          }
                          secondary={onboardingStatus?.users?.info}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {Boolean(users?.length) && !isUsersReadyForSubmit && (
                    <Grid item xs={12} lg={10}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() =>
                          history.push(`${TR_LINK}/profile/${partner?.id}`, {
                            tab: 2,
                          })
                        }
                      >
                        COMPLETE
                      </Button>
                    </Grid>
                  )}
                  {!Boolean(users?.length) && (
                    <Grid item xs={12} lg={2}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() =>
                          history.push(`${TR_LINK}/profile/${partner?.id}`, {
                            tab: 2,
                          })
                        }
                      >
                        REGISTER
                      </Button>
                    </Grid>
                  )}
                </Grid>
              )}
            </ListItemButton>
          </List>
        </Collapse>
      </Grid>
    </Grid>
  );
};
export default SignedPartner;
